import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Seo from "@/components/Seo";
import {
  ArrowRight,
  MapPin,
  Compass,
  Users,
  ShieldCheck,
  DollarSign,
  Sparkles,
  Handshake,
  Landmark,
  Bus,
  Utensils,
  Camera,
  ClipboardCheck,
  Phone,
  Mail,
  Plane,
  BadgeCheck,
  Star,
  Quote,
} from "lucide-react";
import Reveal from "@/components/motion/Reveal";
import HexMark from "@/components/motion/HexMark";
import DmcWorldMap from "@/components/DmcWorldMap";

const LOGO = "/dmc/logo.png";

const BENEFITS = [
  {
    icon: MapPin,
    k: "Local ground intelligence",
    body:
      "Boots-on-the-ground expertise in every destination — venues, vendors, permits, culture — nothing gets lost in translation.",
  },
  {
    icon: DollarSign,
    k: "Real cost leverage",
    body:
      "Regional buying power and long-standing supplier relationships translate to preferred rates you can’t get through a corporate desk.",
  },
  {
    icon: ShieldCheck,
    k: "Risk absorbed",
    body:
      "Contracts vetted, insurance verified, permits pulled. Local liability handled so your legal exposure stays contained.",
  },
  {
    icon: Handshake,
    k: "One accountable partner",
    body:
      "Single point of contact for every local moving piece — hotel, transport, activation, staffing — no vendor sprawl.",
  },
  {
    icon: Users,
    k: "Curated vendor networks",
    body:
      "Only pre-vetted suppliers with a track record. We show up with the shortlist, not the phone book.",
  },
  {
    icon: Sparkles,
    k: "Cultural authenticity",
    body:
      "Programming that feels of the place — not stock experiences. Every touchpoint is designed to land locally.",
  },
];

const PROCESS = [
  {
    n: "01",
    title: "Discovery",
    body:
      "We start with your objectives, audience, and budget — then map the destination against them. What matters, what doesn’t, what we optimize for.",
  },
  {
    n: "02",
    title: "Curation",
    body:
      "A shortlist of venues, hotels, transport, F&B, and off-site experiences — hand-picked, negotiated, and scored side-by-side.",
  },
  {
    n: "03",
    title: "Coordination",
    body:
      "One project timeline. One operating dossier. Every vendor briefed, every deadline tracked, every risk logged.",
  },
  {
    n: "04",
    title: "On-site Execution",
    body:
      "Certified travel directors and floor teams in-market — radios up, timelines rehearsed, contingencies briefed.",
  },
  {
    n: "05",
    title: "Recap & Handoff",
    body:
      "Post-event debrief with financials, attendee analytics, and vendor scorecards — documented, exportable, portable.",
  },
];

const SERVICES = [
  {
    icon: Landmark,
    name: "Venue & Off-site Sourcing",
    body: "Ballrooms, private residences, cultural landmarks — vetted and negotiated in-market.",
  },
  {
    icon: Bus,
    name: "Ground Transportation",
    body: "Shuttle networks, executive cars, and VIP motorcades built around your run-of-show.",
  },
  {
    icon: Utensils,
    name: "F&B and Catering",
    body: "Local chefs, dietary compliance, buffet-to-plated logistics — with real cost control.",
  },
  {
    icon: Compass,
    name: "Off-site Experiences",
    body: "Cultural tours, teambuilding, private dinners — programming that feels of the place.",
  },
  {
    icon: Users,
    name: "Local Staff & Directors",
    body: "Multilingual travel directors, greeters, and floor teams staffed and trained.",
  },
  {
    icon: Camera,
    name: "Production & Décor",
    body: "AV, lighting, floral, branding — sourced through pre-vetted local production partners.",
  },
  {
    icon: ClipboardCheck,
    name: "Permits & Compliance",
    body: "Local permits, insurance, and regulatory paperwork handled end-to-end.",
  },
  {
    icon: Plane,
    name: "Incentive & Group Travel",
    body: "Rewards trips, sales incentives, executive retreats — designed and delivered.",
  },
];

export default function MeDmc() {
  useEffect(() => {
    const prev = document.title;
    document.title = "M.E. DMC — Destination Management Company · Magee Enterprises";
    return () => {
      document.title = prev;
    };
  }, []);

  // Rotating final word in headline
  const rotatingWords = ["mastery.", "craft.", "delivery.", "precision."];
  const [wordIdx, setWordIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setWordIdx((v) => (v + 1) % rotatingWords.length), 2600);
    return () => clearInterval(t);
  }, [rotatingWords.length]);

  // Marquee destinations
  const marqueeCities = [
    "New York", "Paris", "Tokyo", "London", "Rio de Janeiro",
    "Barcelona", "Sydney", "Copenhagen", "Zurich", "Dublin",
    "San Juan", "Hyderabad", "Boston", "Dallas", "Amsterdam",
    "Mexico City", "Miami", "Chicago", "Rome", "Berlin",
  ];

  // Orbital pins (city, ring, initial angle)
  const orbitPins = [
    { label: "NYC", ring: 0, angle: 20 },
    { label: "LDN", ring: 0, angle: 140 },
    { label: "TYO", ring: 0, angle: 260 },
    { label: "PAR", ring: 1, angle: 60 },
    { label: "SYD", ring: 1, angle: 210 },
    { label: "COP", ring: 1, angle: 330 },
    { label: "DUB", ring: 2, angle: 90 },
    { label: "HYD", ring: 2, angle: 270 },
  ];

  return (
    <div className="bg-white text-[var(--text)]">
      <Seo
        title="M.E. DMC — Destination Management Company"
        description="M.E. DMC is the specialized Destination Management Company division of Magee Enterprises — local logistics, planning, and coordination for corporate meetings, incentives, and special events."
        path="/services/me-dmc"
        image={LOGO}
        keywords="DMC, destination management company, local logistics, incentive travel, off-site experiences, corporate meetings, ground transportation, event coordination"
      />

      {/* HERO */}
      <section className="relative pt-28 md:pt-32 px-4 md:px-6">
        <div className="relative max-w-[1600px] mx-auto rounded-[28px] md:rounded-[36px] overflow-hidden bg-[#050A14]">
          {/* Ambient background */}
          <div className="absolute inset-0 grid-pattern-dark opacity-50" />
          <div className="absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full bg-cyan-400/20 blur-3xl" />
          <div className="absolute -bottom-40 -right-24 w-[620px] h-[620px] rounded-full bg-sky-500/20 blur-3xl" />
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[70%] h-[300px] cyan-radial" />
          <HexMark
            size={280}
            opacity={0.06}
            className="absolute -right-8 top-16 rotate-[16deg] pointer-events-none hidden lg:block"
          />

          {/* Top status ticker */}
          <div className="relative border-b border-white/10 backdrop-blur px-5 md:px-10 py-3 flex items-center justify-between text-[10px] font-mono-brand uppercase tracking-[0.24em] text-white/60 z-10">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-400" />
              </span>
              <span className="text-cyan-300">Global Desk · Live</span>
            </div>
            <div className="hidden sm:flex items-center gap-5">
              <span>NYC 09:00</span>
              <span className="hidden md:inline text-white/25">·</span>
              <span className="hidden md:inline">LDN 14:00</span>
              <span className="hidden lg:inline text-white/25">·</span>
              <span className="hidden lg:inline">TYO 22:00</span>
              <span className="hidden lg:inline text-white/25">·</span>
              <span className="hidden lg:inline">COP 15:00</span>
            </div>
            <div className="text-white/50">M.E. DMC / 001</div>
          </div>

          {/* Split hero grid */}
          <div className="relative grid grid-cols-12 gap-6 lg:gap-10 px-5 md:px-12 lg:px-16 pt-14 md:pt-20 pb-10 md:pb-12">
            {/* LEFT — copy + kinetic headline */}
            <div className="col-span-12 lg:col-span-7 relative z-10">
              <Reveal>
                <span className="chip !bg-white/10 !text-cyan-300 !border-cyan-300/25">
                  <MapPin size={12} /> Destination Management Company
                </span>
              </Reveal>

              <Reveal delay={0.1}>
                <h1
                  data-testid="dmc-hero-headline"
                  className="hero-headline text-white mt-6 md:mt-8 leading-[0.92] text-[46px] sm:text-[62px] md:text-[76px] lg:text-[86px] xl:text-[100px] tracking-[-0.03em] text-balance"
                >
                  Global reach.
                  <br />
                  <span className="inline-block text-white/40 mr-3 md:mr-4">Local</span>
                  <span className="relative inline-block align-baseline">
                    <AnimatePresence mode="wait">
                      <motion.span
                        key={rotatingWords[wordIdx]}
                        initial={{ y: 40, opacity: 0, rotateX: -60 }}
                        animate={{ y: 0, opacity: 1, rotateX: 0 }}
                        exit={{ y: -40, opacity: 0, rotateX: 60 }}
                        transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                        className="inline-block bg-gradient-to-r from-cyan-300 via-sky-300 to-cyan-400 bg-clip-text text-transparent italic font-medium"
                        style={{ transformOrigin: "bottom center" }}
                      >
                        {rotatingWords[wordIdx]}
                      </motion.span>
                    </AnimatePresence>
                    {/* underline shimmer */}
                    <span className="absolute -bottom-2 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-cyan-400 to-transparent" />
                  </span>
                </h1>
              </Reveal>

              <Reveal delay={0.2}>
                <p className="mt-8 text-white/80 text-base md:text-lg leading-relaxed max-w-xl">
                  The specialized Destination Management Company division of
                  Magee Enterprises. Local logistics, planning, and
                  coordination for corporate meetings, incentives, and special
                  events — in every destination we work.
                </p>
              </Reveal>

              <Reveal delay={0.3}>
                <div className="mt-8 md:mt-10 flex flex-wrap items-center gap-4">
                  <Link
                    to="/contact"
                    data-testid="dmc-hero-cta-quote"
                    className="btn-primary"
                  >
                    Request a Quote <ArrowRight size={16} />
                  </Link>
                  <a
                    href="tel:+18882992250"
                    className="btn-dark backdrop-blur border-white/25"
                  >
                    <Phone size={14} /> 888-299-2250
                  </a>
                </div>
              </Reveal>

              {/* Micro stat row */}
              <Reveal delay={0.4}>
                <div className="mt-10 md:mt-14 grid grid-cols-3 max-w-md gap-6 pt-6 border-t border-white/10">
                  {[
                    { k: "28+", v: "Destinations" },
                    { k: "17+", v: "Countries" },
                    { k: "5+", v: "Continents" },
                  ].map((s) => (
                    <div key={s.v}>
                      <div className="font-editorial text-3xl md:text-4xl bg-gradient-to-r from-cyan-300 to-sky-400 bg-clip-text text-transparent leading-none">
                        {s.k}
                      </div>
                      <div className="mt-2 font-mono-brand text-[9.5px] uppercase tracking-[0.24em] text-white/50">
                        {s.v}
                      </div>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>

            {/* RIGHT — orbital scene */}
            <div className="col-span-12 lg:col-span-5 relative flex items-center justify-center min-h-[440px] lg:min-h-[560px]">
              <Reveal>
                <div className="relative aspect-square w-[320px] sm:w-[380px] md:w-[440px] lg:w-[460px] max-w-full">
                  {/* Corner brackets */}
                  <span className="corner-cross" style={{ top: 0, left: 0 }} />
                  <span className="corner-cross" style={{ top: 0, right: 0 }} />
                  <span className="corner-cross" style={{ bottom: 0, left: 0 }} />
                  <span className="corner-cross" style={{ bottom: 0, right: 0 }} />

                  {/* Concentric rings — each rotates independently */}
                  {[0, 1, 2].map((ringIdx) => {
                    const scale = 1 - ringIdx * 0.18; // 1.0, 0.82, 0.64
                    const animClass = ringIdx === 0
                      ? "dmc-orbit-anim-slow"
                      : ringIdx === 1
                        ? "dmc-orbit-anim-med"
                        : "dmc-orbit-anim-fast";
                    return (
                      <div
                        key={ringIdx}
                        className={`dmc-orbit-ring ${animClass}`}
                        style={{ transform: `scale(${scale})` }}
                      >
                        {orbitPins
                          .filter((p) => p.ring === ringIdx)
                          .map((p, i) => {
                            const radius = 50; // percent of ring
                            const rad = (p.angle * Math.PI) / 180;
                            const x = 50 + radius * Math.cos(rad);
                            const y = 50 + radius * Math.sin(rad);
                            return (
                              <span
                                key={p.label + i}
                                className="dmc-pin"
                                data-label={p.label}
                                style={{
                                  left: `${x}%`,
                                  top: `${y}%`,
                                }}
                              />
                            );
                          })}
                      </div>
                    );
                  })}

                  {/* Ambient dot cluster */}
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute top-[8%] left-[42%] w-1 h-1 rounded-full bg-cyan-300/70" />
                    <div className="absolute top-[85%] left-[20%] w-1 h-1 rounded-full bg-cyan-300/50" />
                    <div className="absolute top-[35%] right-[6%] w-1 h-1 rounded-full bg-cyan-300/60" />
                  </div>

                  {/* Central logo with pulse halo */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="relative">
                      <div className="absolute inset-0 rounded-full bg-cyan-400/30 blur-3xl scale-125 animate-pulse" />
                      <div
                        className="absolute inset-[-18px] rounded-full border border-cyan-300/40"
                        style={{
                          boxShadow:
                            "0 0 30px rgba(6,182,212,0.35), inset 0 0 20px rgba(6,182,212,0.15)",
                        }}
                      />
                      <img
                        src={LOGO}
                        alt="M.E. DMC — a Destination Management Company"
                        data-testid="dmc-hero-logo"
                        className="relative w-32 h-32 md:w-40 md:h-40 lg:w-44 lg:h-44 object-contain drop-shadow-[0_10px_40px_rgba(6,182,212,0.5)]"
                      />
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>

          {/* Bottom destinations marquee */}
          <div className="relative border-t border-white/10 py-4 overflow-hidden group">
            <div className="dmc-marquee">
              {[...marqueeCities, ...marqueeCities].map((c, i) => (
                <span
                  key={i}
                  className="flex items-center gap-6 pr-6 font-mono-brand text-[11px] md:text-[12px] uppercase tracking-[0.28em] text-white/45 shrink-0"
                >
                  <span>{c}</span>
                  <span className="w-1 h-1 rounded-full bg-cyan-400/70" />
                </span>
              ))}
            </div>
            {/* Fade masks */}
            <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#050A14] to-transparent pointer-events-none" />
            <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#050A14] to-transparent pointer-events-none" />
          </div>
        </div>
      </section>

      {/* INTRO / DEFINITION */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />
        <div className="brand-bloom brand-bloom-cyan w-[500px] h-[500px] -top-32 -left-40 opacity-30" />
        <div className="brand-bloom brand-bloom-blue w-[420px] h-[420px] -bottom-24 -right-24 opacity-25" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-12 gap-8 md:gap-12 items-center">
            <Reveal className="col-span-12 md:col-span-5">
              <div className="relative rounded-3xl overflow-hidden aspect-[4/5] md:aspect-auto md:min-h-[560px]">
                {/* NJ skyline photo */}
                <img
                  src="https://images.unsplash.com/photo-1522083165195-3424ed129620?w=1600&auto=format&fit=crop&q=80"
                  alt="Manhattan skyline seen across the Hudson River from Jersey City, New Jersey — the M.E. DMC home ground."
                  data-testid="dmc-nj-hero-photo"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                {/* Dark gradient wash for legibility */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#050A14]/70 via-[#050A14]/20 to-[#0EA5E9]/25" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050A14]/85 via-transparent to-[#050A14]/40" />

                {/* Corner cross accents */}
                <span className="corner-cross" style={{ top: 14, left: 14 }} />
                <span className="corner-cross" style={{ bottom: 14, right: 14 }} />

                {/* Top-left HQ chip */}
                <div className="absolute top-5 left-5">
                  <span className="chip !bg-white/10 !text-cyan-200 !border-white/25 backdrop-blur">
                    <MapPin size={12} /> HQ · New Jersey
                  </span>
                </div>

                {/* Diagonal cyan stripe with mounted logo (bottom-right) */}
                <div
                  aria-hidden
                  className="absolute -right-8 bottom-16 md:-right-6 md:bottom-24 h-[2px] w-56 bg-gradient-to-r from-transparent via-cyan-400/70 to-cyan-300 rotate-[-18deg] origin-right"
                  style={{ filter: "drop-shadow(0 0 12px rgba(6,182,212,0.6))" }}
                />

                {/* Floating DMC logo medallion — creative overlay */}
                <div className="absolute bottom-5 right-5 md:bottom-6 md:right-6">
                  <div className="relative">
                    {/* Outer glow ring */}
                    <div className="absolute inset-0 rounded-full bg-cyan-400/40 blur-2xl scale-125 animate-pulse" />
                    {/* Cyan halo ring */}
                    <div
                      className="absolute inset-[-6px] rounded-full border border-cyan-300/70"
                      style={{
                        boxShadow:
                          "0 0 24px rgba(6,182,212,0.55), inset 0 0 12px rgba(6,182,212,0.25)",
                      }}
                    />
                    <img
                      src={LOGO}
                      alt="M.E. DMC logomark"
                      className="relative w-28 h-28 md:w-36 md:h-36 object-contain drop-shadow-[0_18px_40px_rgba(6,182,212,0.5)]"
                      style={{ transform: "rotate(-4deg)" }}
                    />
                  </div>
                </div>

                {/* Bottom-left tagline */}
                <div className="absolute bottom-6 left-6 md:bottom-8 md:left-8 max-w-[52%]">
                  <div className="font-mono-brand text-[10px] uppercase tracking-[0.28em] text-cyan-300/85 mb-2">
                    Est. Global Ops
                  </div>
                  <div className="font-editorial text-white text-xl md:text-2xl leading-tight tracking-[-0.02em]">
                    Local expertise.{" "}
                    <span className="italic font-medium bg-gradient-to-r from-cyan-300 to-sky-400 bg-clip-text text-transparent">
                      Global reach.
                    </span>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1} className="col-span-12 md:col-span-7">
              <span className="chip">
                <span className="chip-dot" /> A division of Magee Enterprises
              </span>
              <h2 className="mt-6 font-editorial text-4xl md:text-6xl leading-[1] tracking-[-0.03em] text-balance">
                Local logistics —{" "}
                <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">
                  handled like a local, delivered like an agency.
                </span>
              </h2>
              <p className="mt-6 text-[var(--text-muted)] text-lg leading-relaxed">
                A DMC is your on-the-ground production partner in-market — the
                team that knows which venue closes at midnight, which caterer
                won&rsquo;t skimp on service, and which permit office you need
                to walk into on a Tuesday. M.E. DMC brings that intelligence
                to every corporate meeting, incentive trip, and special event
                we help you produce.
              </p>
              <p className="mt-4 text-[var(--text-muted)] text-lg leading-relaxed">
                Backed by the full agency of Magee Enterprises — certified
                project management, event technology, and executive-grade
                logistics — you get global reach with local mastery, under a
                single accountable roof.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="relative py-20 md:py-28 bg-[var(--surface)] overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />
        <div className="brand-bloom brand-bloom-cyan w-[500px] h-[500px] -top-32 -right-24 opacity-30" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <span className="chip">
              <span className="chip-dot" /> Benefits
            </span>
            <h2 className="mt-6 font-editorial text-4xl md:text-6xl leading-[1] tracking-[-0.03em] max-w-3xl text-balance">
              Why partner with{" "}
              <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">
                M.E. DMC.
              </span>
            </h2>
            <p className="mt-6 text-[var(--text-muted)] max-w-2xl">
              A dedicated in-market team compresses your timeline, reduces your
              risk, and delivers programming that feels authentically of the
              destination — not shipped in from a corporate desk.
            </p>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.k} delay={i * 0.06}>
                <div
                  data-testid={`dmc-benefit-${i + 1}`}
                  className="grad-border card-white p-6 md:p-8 h-full relative"
                >
                  <span className="corner-cross" style={{ top: 10, right: 10 }} />
                  <div className="icon-tile mb-6 relative z-10">
                    <b.icon size={20} />
                  </div>
                  <div className="numeral-mono text-4xl md:text-5xl absolute top-3 right-4 select-none">
                    0{i + 1}
                  </div>
                  <h3 className="font-editorial text-xl leading-tight">
                    {b.k}
                  </h3>
                  <p className="mt-3 text-[var(--text-muted)] text-sm leading-relaxed">
                    {b.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />
        <div className="brand-bloom brand-bloom-blue w-[420px] h-[420px] -top-20 -left-24 opacity-25" />
        <div className="brand-bloom brand-bloom-cyan w-[420px] h-[420px] -bottom-32 -right-24 opacity-25" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
              <div>
                <span className="chip">
                  <span className="chip-dot" /> Our Process
                </span>
                <h2 className="mt-6 font-editorial text-4xl md:text-6xl leading-[1] tracking-[-0.03em] max-w-3xl text-balance">
                  From destination brief to{" "}
                  <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">
                    strike & debrief.
                  </span>
                </h2>
              </div>
              <p className="text-[var(--text-muted)] max-w-sm">
                Five disciplined phases. One accountable owner per line item.
                Zero ambiguity.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5">
            {PROCESS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.06}>
                <div
                  data-testid={`dmc-process-${p.n}`}
                  className="relative card-white p-6 h-full flex flex-col"
                >
                  <span className="numeral-mono text-5xl md:text-6xl text-cyan-500/70 leading-none">
                    {p.n}
                  </span>
                  <h3 className="mt-6 font-editorial text-xl leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-[var(--text-muted)] text-[13px] leading-relaxed">
                    {p.body}
                  </p>
                  {i < PROCESS.length - 1 && (
                    <span className="hidden lg:block absolute top-1/2 -right-3 w-6 h-px bg-gradient-to-r from-cyan-500/40 to-transparent" />
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* EXPLORE SERVICES */}
      <section className="relative py-20 md:py-28 bg-[var(--surface)] overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />
        <HexMark
          size={220}
          opacity={0.05}
          className="absolute -left-6 top-16 rotate-[10deg] pointer-events-none hidden lg:block"
        />

        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
              <div>
                <span className="chip">
                  <span className="chip-dot" /> Explore Services
                </span>
                <h2 className="mt-6 font-editorial text-4xl md:text-6xl leading-[1] tracking-[-0.03em] max-w-3xl text-balance">
                  Eight ways we work the{" "}
                  <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">
                    destination for you.
                  </span>
                </h2>
              </div>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {SERVICES.map((s, i) => (
              <Reveal key={s.name} delay={i * 0.04}>
                <div
                  data-testid={`dmc-service-${i + 1}`}
                  className="card-white p-6 h-full"
                >
                  <div className="icon-tile mb-5">
                    <s.icon size={18} />
                  </div>
                  <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)]">
                    /{String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-editorial text-lg leading-tight mt-1">
                    {s.name}
                  </h3>
                  <p className="mt-3 text-[var(--text-muted)] text-[13px] leading-relaxed">
                    {s.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DESTINATION GALLERY */}
      <section className="relative py-20 md:py-28 bg-[#050A14] text-white overflow-hidden border-t border-white/5">
        <div className="absolute inset-0 grid-pattern-dark opacity-30 pointer-events-none" />
        <div className="absolute -top-24 left-1/3 w-[420px] h-[420px] rounded-full bg-cyan-500/10 blur-3xl" />
        <div className="absolute -bottom-24 right-1/4 w-[420px] h-[420px] rounded-full bg-sky-500/10 blur-3xl" />

        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
              <div>
                <span className="chip !bg-white/5 !text-cyan-300 !border-cyan-300/25">
                  <Compass size={12} /> Destination portfolio
                </span>
                <h2 className="mt-6 font-editorial text-4xl md:text-6xl leading-[1] tracking-[-0.03em] max-w-3xl text-balance">
                  Cities we{" "}
                  <span className="italic font-medium bg-gradient-to-r from-cyan-300 to-sky-400 bg-clip-text text-transparent">
                    know by heart.
                  </span>
                </h2>
              </div>
              <p className="text-white/60 max-w-sm text-[14.5px] leading-relaxed">
                A few of the destinations where our in-market teams have run
                point — venues secured, permits pulled, guests moved on time.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-12 gap-3 md:gap-4 auto-rows-[180px] md:auto-rows-[240px]">
            {[
              {
                city: "New York",
                country: "USA",
                src: "https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=1600&auto=format&fit=crop&q=80",
                span: "col-span-12 md:col-span-6 md:row-span-2",
                tag: "01",
              },
              {
                city: "Paris",
                country: "France",
                src: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1200&auto=format&fit=crop&q=80",
                span: "col-span-6 md:col-span-3 md:row-span-1",
                tag: "02",
              },
              {
                city: "Tokyo",
                country: "Japan",
                src: "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=1200&auto=format&fit=crop&q=80",
                span: "col-span-6 md:col-span-3 md:row-span-1",
                tag: "03",
              },
              {
                city: "London",
                country: "UK",
                src: "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=1200&auto=format&fit=crop&q=80",
                span: "col-span-6 md:col-span-3 md:row-span-1",
                tag: "04",
              },
              {
                city: "Rio de Janeiro",
                country: "Brazil",
                src: "https://images.unsplash.com/photo-1483729558449-99ef09a8c325?w=1200&auto=format&fit=crop&q=80",
                span: "col-span-6 md:col-span-3 md:row-span-1",
                tag: "05",
              },
              {
                city: "Barcelona",
                country: "Spain",
                src: "https://images.unsplash.com/photo-1583422409516-2895a77efded?w=1200&auto=format&fit=crop&q=80",
                span: "col-span-6 md:col-span-4 md:row-span-1",
                tag: "06",
              },
              {
                city: "Sydney",
                country: "Australia",
                src: "https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=1200&auto=format&fit=crop&q=80",
                span: "col-span-6 md:col-span-4 md:row-span-1",
                tag: "07",
              },
              {
                city: "Copenhagen",
                country: "Denmark",
                src: "https://images.unsplash.com/photo-1513622470522-26c3c8a854bc?w=1200&auto=format&fit=crop&q=80",
                span: "col-span-12 md:col-span-4 md:row-span-1",
                tag: "08",
              },
            ].map((p) => (
              <div
                key={p.city}
                data-testid={`dmc-destination-${p.city.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`${p.span} group relative overflow-hidden rounded-2xl border border-white/10`}
              >
                <img
                  src={p.src}
                  alt={`${p.city}, ${p.country}`}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.06]"
                />
                {/* Base gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#050A14]/95 via-[#050A14]/30 to-transparent" />
                {/* Cyan sheen on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/0 to-sky-500/0 group-hover:from-cyan-500/15 group-hover:to-sky-500/10 transition-colors duration-500" />

                {/* Corner tag */}
                <span className="absolute top-3 left-3 chip !bg-white/10 !text-cyan-200 !border-white/20 !py-1 !px-2 !text-[9px] backdrop-blur">
                  /{p.tag}
                </span>

                {/* Label */}
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-cyan-300/85 mb-1">
                    {p.country}
                  </div>
                  <div className="font-editorial text-xl md:text-2xl lg:text-3xl leading-tight tracking-[-0.02em] text-white">
                    {p.city}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <Reveal delay={0.3}>
            <p className="mt-10 text-center text-white/50 text-[13px] italic">
              A small sample. See the full map below for every destination
              we&rsquo;ve produced in.
            </p>
          </Reveal>
        </div>
      </section>

      {/* WORLD MAP — Where we've delivered */}
      <section className="relative py-20 md:py-28 bg-[#050A14] text-white overflow-hidden">
        <div className="absolute inset-0 grid-pattern-dark opacity-40 pointer-events-none" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[70%] h-[300px] cyan-radial" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
              <span className="chip !bg-white/5 !text-cyan-300 !border-cyan-300/25">
                <MapPin size={12} /> Global footprint
              </span>
              <h2 className="mt-6 font-editorial text-4xl md:text-6xl leading-[1] tracking-[-0.03em] text-balance">
                Where we&rsquo;ve{" "}
                <span className="italic font-medium bg-gradient-to-r from-cyan-300 to-sky-400 bg-clip-text text-transparent">
                  delivered.
                </span>
              </h2>
              <p className="mt-6 text-white/70 leading-relaxed">
                We&rsquo;re ready to explore new destinations with you.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <DmcWorldMap />
          </Reveal>
        </div>
      </section>

      {/* CLIENT TESTIMONIALS — Destination-flavored (light theme with 3D bg) */}
      <section className="relative py-24 md:py-32 bg-white text-[var(--text)] overflow-hidden">
        {/* Ambient blooms — depth layer 1 */}
        <div className="brand-bloom brand-bloom-cyan w-[520px] h-[520px] -top-32 -left-20 opacity-45" />
        <div className="brand-bloom brand-bloom-blue w-[460px] h-[460px] top-1/3 -right-20 opacity-30" />
        <div className="brand-bloom brand-bloom-navy w-[420px] h-[420px] -bottom-24 left-1/3 opacity-25" />

        {/* Hex pattern — depth layer 2 */}
        <div className="absolute inset-0 hex-pattern opacity-50 pointer-events-none" />

        {/* Giant decorative Quote glyph — floating 3D element */}
        <div
          aria-hidden
          className="absolute -left-6 top-16 md:left-8 md:top-10 pointer-events-none select-none"
          style={{
            transform: "perspective(800px) rotateY(-15deg) rotateX(6deg)",
            filter: "drop-shadow(0 30px 40px rgba(11,37,69,0.12))",
          }}
        >
          <Quote
            size={220}
            strokeWidth={1.2}
            className="text-cyan-400/25"
          />
        </div>
        <div
          aria-hidden
          className="absolute -right-6 bottom-10 md:right-10 md:bottom-16 pointer-events-none select-none"
          style={{
            transform: "perspective(800px) rotateY(180deg) rotateZ(-6deg) rotateX(-6deg)",
            filter: "drop-shadow(0 30px 40px rgba(6,182,212,0.15))",
          }}
        >
          <Quote
            size={240}
            strokeWidth={1.2}
            className="text-sky-400/20"
          />
        </div>

        {/* Floating hex marks — depth layer 3 */}
        <HexMark
          size={200}
          opacity={0.06}
          className="absolute right-1/4 top-24 rotate-[18deg] pointer-events-none hidden lg:block"
        />
        <HexMark
          size={160}
          opacity={0.05}
          className="absolute left-1/4 bottom-24 rotate-[-12deg] pointer-events-none hidden lg:block"
        />

        {/* Orbital ring — subtle 3D ring */}
        <div
          aria-hidden
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none hidden md:block"
          style={{
            width: 900,
            height: 900,
            borderRadius: "50%",
            border: "1px dashed rgba(6, 182, 212, 0.12)",
            transform: "translate(-50%, -50%) perspective(1200px) rotateX(72deg)",
          }}
        />
        <div
          aria-hidden
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none hidden md:block"
          style={{
            width: 1200,
            height: 1200,
            borderRadius: "50%",
            border: "1px solid rgba(11, 37, 69, 0.06)",
            transform: "translate(-50%, -50%) perspective(1200px) rotateX(72deg)",
          }}
        />

        {/* Corner cross accents */}
        <span className="corner-cross" style={{ top: 40, right: 40 }} />
        <span className="corner-cross" style={{ bottom: 40, left: 40 }} />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
              <span className="chip">
                <Quote size={12} /> Client voices
              </span>
              <h2 className="mt-6 font-editorial text-4xl md:text-6xl leading-[1] tracking-[-0.03em] text-balance">
                What clients say after we{" "}
                <span className="italic font-medium bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent">
                  strike the room.
                </span>
              </h2>
              <p className="mt-6 text-[var(--text-muted)] leading-relaxed">
                Real words from partners we&rsquo;ve produced with — across
                global meetings, incentives, and corporate destinations.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-7 relative">
            {[
              {
                quote:
                  "Dion is a very knowledgeable, dedicated, and experienced Project Manager with both domestic and international experience. He does not hesitate to take on new challenges while offering creative solutions for his clients. Extremely personable — an asset to anyone looking for a seasoned meeting professional.",
                name: "Joyce Wooden",
                role: "Sr. Manager, Global Meetings & Events",
                photo: "/testimonials/person-6.jpeg",
                initials: "JW",
              },
              {
                quote:
                  "It has been my pleasure to get to know and work for Sir Dion. He has the ability to share clear messages and make complex ideas easy to understand for people from around the world working for him. I always felt his support — I am very grateful for that.",
                name: "Pita Cruz y Curro",
                role: "Especialista en Marketing, Comunicación y Eventos — Volvo Group",
                photo: "/testimonials/person-8.jpeg",
                initials: "PC",
              },
              {
                quote:
                  "Phenomenal through the planning and execution. Consistent updates, meaningful inputs, and effective budget management using highly proficient negotiating skills to save money while optimizing the attendees' experience. Looking forward to next year's event.",
                name: "Dr. Tiffani (Braswell) Worthy, PMP",
                role: "Executive Coach · DEI Strategist — Day & Zimmermann",
                photo: "/testimonials/person-5.jpeg",
                initials: "TW",
              },
            ].map((t, i) => (
              <Reveal key={t.name} delay={i * 0.08}>
                <figure
                  data-testid={`dmc-testimonial-${i + 1}`}
                  className={`group relative h-full grad-border card-white px-6 md:px-7 py-8 md:py-10 flex flex-col overflow-hidden transition-all duration-500 hover:-translate-y-1 ${
                    i === 1 ? "md:mt-6" : ""
                  }`}
                  style={{
                    boxShadow:
                      "0 20px 50px -25px rgba(11, 37, 69, 0.18), 0 8px 20px -12px rgba(6, 182, 212, 0.12)",
                  }}
                >
                  {/* Corner glow */}
                  <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-cyan-500/12 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                  <span className="corner-cross" style={{ top: 12, right: 12 }} />

                  {/* Icon tile — matches site's design language */}
                  <div className="icon-tile mb-5 relative z-10">
                    <Quote size={16} />
                  </div>

                  {/* 5-star row */}
                  <div
                    className="relative flex items-center gap-1 text-[#FBBF24]"
                    aria-label="5 star rating"
                  >
                    {[0, 1, 2, 3, 4].map((s) => (
                      <Star
                        key={s}
                        size={13}
                        fill="currentColor"
                        strokeWidth={0}
                      />
                    ))}
                  </div>

                  <blockquote className="relative mt-5 text-[var(--text)] text-[14.5px] leading-relaxed flex-1">
                    &ldquo;{t.quote}&rdquo;
                  </blockquote>

                  <figcaption className="relative mt-6 flex items-center gap-3 pt-5 border-t border-[var(--border)]">
                    {t.photo ? (
                      <img
                        src={t.photo}
                        alt={t.name}
                        loading="lazy"
                        className="w-11 h-11 rounded-full object-cover border border-cyan-500/25 ring-2 ring-white shadow-[0_6px_18px_-8px_rgba(6,182,212,0.35)]"
                      />
                    ) : (
                      <span className="w-11 h-11 rounded-full bg-gradient-to-br from-cyan-500 to-sky-600 flex items-center justify-center font-mono-brand text-[13px] text-white shrink-0 shadow-[0_6px_18px_-8px_rgba(6,182,212,0.5)]">
                        {t.initials}
                      </span>
                    )}
                    <div className="min-w-0">
                      <div className="font-editorial text-[15px] leading-tight text-[var(--text)] truncate">
                        {t.name}
                      </div>
                      <div className="mt-0.5 font-mono-brand text-[10px] uppercase tracking-[0.18em] text-[var(--text-muted)] truncate">
                        {t.role}
                      </div>
                    </div>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>


      {/* CERTIFICATIONS — Credentials strip */}
      <section className="relative py-20 md:py-28 bg-[#050A14] text-white overflow-hidden border-t border-white/5">
        <div className="absolute inset-0 grid-pattern-dark opacity-30 pointer-events-none" />
        <div className="absolute -top-20 -left-24 w-[420px] h-[420px] rounded-full bg-cyan-500/12 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 w-[420px] h-[420px] rounded-full bg-sky-500/12 blur-3xl" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
              <span className="chip !bg-white/5 !text-cyan-300 !border-cyan-300/25">
                <BadgeCheck size={12} /> Certified expertise
              </span>
              <h2 className="mt-6 font-editorial text-4xl md:text-6xl leading-[1] tracking-[-0.03em] text-balance">
                Credentials that{" "}
                <span className="italic font-medium bg-gradient-to-r from-cyan-300 to-sky-400 bg-clip-text text-transparent">
                  travel with us.
                </span>
              </h2>
              <p className="mt-6 text-white/70 leading-relaxed">
                Every M.E. DMC engagement is anchored by senior credentials in
                meeting management, project delivery, travel direction, and
                healthcare compliance — so risk stays low and delivery stays
                sharp, in any market.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {[
              { acronym: "CMP", name: "Certified Meeting Professional" },
              { acronym: "PMP", name: "Project Management Professional" },
              { acronym: "CMM", name: "Certificate of Meeting Management" },
              { acronym: "CTD", name: "Certified Travel Director" },
              { acronym: "HMCC", name: "Healthcare Meeting Compliance Certificate" },
              { acronym: "CTA", name: "Certified Travel Associate" },
            ].map((c, i) => (
              <Reveal key={c.acronym} delay={i * 0.05}>
                <div
                  data-testid={`dmc-cert-${c.acronym.toLowerCase()}`}
                  className="group relative rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-cyan-300/30 backdrop-blur px-5 py-6 md:px-6 md:py-7 h-full transition-all duration-500 overflow-hidden"
                >
                  {/* subtle corner glow on hover */}
                  <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-cyan-500/15 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

                  <div className="relative flex items-start justify-between gap-3">
                    <div className="font-editorial text-3xl md:text-4xl leading-none bg-gradient-to-br from-cyan-300 to-sky-400 bg-clip-text text-transparent tracking-[-0.02em]">
                      {c.acronym}
                    </div>
                    <BadgeCheck
                      size={16}
                      className="text-cyan-400/70 shrink-0 mt-1 group-hover:text-cyan-300 transition-colors"
                    />
                  </div>
                  <div className="relative mt-4 font-mono-brand text-[10px] md:text-[11px] uppercase tracking-[0.16em] text-white/60 leading-snug">
                    {c.name}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.4}>
            <p className="mt-10 md:mt-12 text-center text-white/50 text-[13px] italic">
              Plus a full portfolio of governance, risk, compliance, and
              sustainability credentials —{" "}
              <a
                href="/leadership"
                className="text-cyan-300 hover:text-cyan-200 underline decoration-cyan-500/40 underline-offset-4 not-italic font-mono-brand text-[11px] uppercase tracking-[0.16em]"
              >
                See leadership
              </a>
              .
            </p>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="card-dark relative overflow-hidden px-8 md:px-16 py-20 md:py-24 text-center">
            <div className="absolute inset-0 grid-pattern-dark opacity-40" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[70%] h-[300px] cyan-radial" />
            <div className="relative">
              <span className="chip !bg-white/5 !text-cyan-300 !border-cyan-300/25">
                <MapPin size={12} /> Ready to break ground in a new market?
              </span>
              <h2 className="mt-8 font-editorial text-4xl md:text-6xl lg:text-7xl leading-[1] tracking-[-0.03em] text-white max-w-4xl mx-auto text-balance">
                Tell us the destination.{" "}
                <span className="bg-gradient-to-r from-cyan-300 to-sky-400 bg-clip-text text-transparent italic font-medium">
                  We&rsquo;ll bring the local muscle.
                </span>
              </h2>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                <Link
                  to="/contact"
                  data-testid="dmc-cta-quote"
                  className="btn-primary"
                >
                  Request a Quote <ArrowRight size={16} />
                </Link>
                <a
                  href="tel:+18882992250"
                  className="btn-dark border-white/20"
                >
                  <Phone size={14} /> Call the agency
                </a>
              </div>

              <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 text-white/80 text-sm max-w-3xl mx-auto">
                <a
                  href="tel:+18882992250"
                  className="flex items-center gap-3 justify-center hover:text-cyan-300 transition-colors"
                >
                  <Phone size={14} className="text-cyan-400" />
                  +1 · 888-299-2250
                </a>
                <a
                  href="mailto:dion@mageeenterprises.com"
                  className="flex items-center gap-3 justify-center hover:text-cyan-300 transition-colors"
                >
                  <Mail size={14} className="text-cyan-400" />
                  dion@mageeenterprises.com
                </a>
                <span className="flex items-center gap-3 justify-center">
                  <MapPin size={14} className="text-cyan-400" />
                  Global operations · HQ North America
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
